import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { getBlogArticles, createBlogArticle, updateBlogArticle, deleteBlogArticle } from '../lib/api';
import { toast } from 'sonner';

const BlogAdminPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    excerpt: '',
    content: '',
    cover_image: '',
    category: 'conseils',
    tags: '',
    is_published: false
  });

  const fetchArticles = async () => {
    try {
      const data = await getBlogArticles(false); // Get all articles including unpublished
      setArticles(data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const resetForm = () => {
    setFormData({
      slug: '',
      title: '',
      excerpt: '',
      content: '',
      cover_image: '',
      category: 'conseils',
      tags: '',
      is_published: false
    });
    setEditingArticle(null);
    setIsEditing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-generate slug from title
    if (name === 'title' && !editingArticle) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSwitchChange = (checked) => {
    setFormData(prev => ({ ...prev, is_published: checked }));
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      slug: article.slug,
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      cover_image: article.cover_image,
      category: article.category,
      tags: article.tags?.join(', ') || '',
      is_published: article.is_published
    });
    setIsEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.slug || !formData.content) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      const articleData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean)
      };

      if (editingArticle) {
        await updateBlogArticle(editingArticle.id, articleData);
        toast.success('Article mis à jour');
      } else {
        await createBlogArticle(articleData);
        toast.success('Article créé');
      }

      resetForm();
      fetchArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      return;
    }

    try {
      await deleteBlogArticle(articleId);
      toast.success('Article supprimé');
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePublish = async (article) => {
    try {
      await updateBlogArticle(article.id, { is_published: !article.is_published });
      toast.success(article.is_published ? 'Article dépublié' : 'Article publié');
      fetchArticles();
    } catch (error) {
      console.error('Error toggling publish:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-ghrb-orange border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="blog-admin-page" className="min-h-screen bg-white">
      {/* Header */}
      <section className="py-12 lg:py-16 bg-ghrb-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-outfit text-xs uppercase tracking-[0.2em] text-ghrb-orange mb-2">
                Administration
              </p>
              <h1 className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight text-ghrb-black">
                Gestion du Blog
              </h1>
            </div>
            <Button
              onClick={() => setIsEditing(true)}
              className="bg-ghrb-orange hover:bg-ghrb-orange-hover text-white rounded-full"
              data-testid="new-article-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouvel article
            </Button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isEditing ? (
            /* Edit Form */
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="max-w-3xl mx-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-outfit text-2xl font-bold text-ghrb-black">
                  {editingArticle ? 'Modifier l\'article' : 'Nouvel article'}
                </h2>
                <Button
                  variant="outline"
                  onClick={resetForm}
                  className="rounded-full"
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-5 h-5 mr-2" />
                  Annuler
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="title" className="font-jakarta">Titre *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Titre de l'article"
                      className="mt-1"
                      data-testid="article-title-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="slug" className="font-jakarta">Slug *</Label>
                    <Input
                      id="slug"
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="url-de-article"
                      className="mt-1"
                      data-testid="article-slug-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="excerpt" className="font-jakarta">Extrait *</Label>
                  <Textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    placeholder="Court résumé de l'article..."
                    rows={2}
                    className="mt-1"
                    data-testid="article-excerpt-input"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="content" className="font-jakarta">Contenu * (Markdown supporté)</Label>
                  <Textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="# Titre&#10;&#10;Votre contenu ici...&#10;&#10;## Sous-titre&#10;&#10;- Point 1&#10;- Point 2"
                    rows={15}
                    className="mt-1 font-mono text-sm"
                    data-testid="article-content-input"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="cover_image" className="font-jakarta">Image de couverture (URL)</Label>
                    <Input
                      id="cover_image"
                      name="cover_image"
                      value={formData.cover_image}
                      onChange={handleInputChange}
                      placeholder="https://example.com/image.jpg"
                      className="mt-1"
                      data-testid="article-image-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category" className="font-jakarta">Catégorie</Label>
                    <Input
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      placeholder="conseils"
                      className="mt-1"
                      data-testid="article-category-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="tags" className="font-jakarta">Tags (séparés par des virgules)</Label>
                  <Input
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder="nettoyage, animaux, astuces"
                    className="mt-1"
                    data-testid="article-tags-input"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    id="is_published"
                    checked={formData.is_published}
                    onCheckedChange={handleSwitchChange}
                    data-testid="article-publish-switch"
                  />
                  <Label htmlFor="is_published" className="font-jakarta cursor-pointer">
                    Publier l'article
                  </Label>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    className="bg-ghrb-orange hover:bg-ghrb-orange-hover text-white rounded-full px-8"
                    data-testid="save-article-btn"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {editingArticle ? 'Mettre à jour' : 'Créer l\'article'}
                  </Button>
                </div>
              </form>
            </motion.div>
          ) : (
            /* Articles List */
            <div className="space-y-4">
              {articles.length === 0 ? (
                <div className="text-center py-16">
                  <p className="font-jakarta text-ghrb-gray-dark mb-4">
                    Aucun article pour le moment.
                  </p>
                  <Button
                    onClick={() => setIsEditing(true)}
                    className="bg-ghrb-orange hover:bg-ghrb-orange-hover text-white rounded-full"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Créer le premier article
                  </Button>
                </div>
              ) : (
                articles.map((article, index) => (
                  <motion.div
                    key={article.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-6 p-6 bg-ghrb-gray rounded-2xl"
                    data-testid={`admin-article-${index}`}
                  >
                    {/* Thumbnail */}
                    {article.cover_image && (
                      <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 hidden sm:block">
                        <img
                          src={article.cover_image}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-outfit font-semibold text-ghrb-black truncate">
                          {article.title}
                        </h3>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          article.is_published 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {article.is_published ? 'Publié' : 'Brouillon'}
                        </span>
                      </div>
                      <p className="font-jakarta text-sm text-ghrb-gray-dark truncate">
                        {article.excerpt}
                      </p>
                      <p className="font-jakarta text-xs text-ghrb-gray-dark mt-1">
                        {formatDate(article.created_at)} • {article.category}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTogglePublish(article)}
                        className="rounded-full"
                        title={article.is_published ? 'Dépublier' : 'Publier'}
                        data-testid={`toggle-publish-${index}`}
                      >
                        {article.is_published ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(article)}
                        className="rounded-full"
                        data-testid={`edit-article-${index}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(article.id)}
                        className="rounded-full text-red-500 hover:bg-red-50"
                        data-testid={`delete-article-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogAdminPage;
